import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { ContractSupplement } from '../entities/ContractSupplement.js';
import { getActiveSectionsForPointInTime } from './getActiveSectionsForPointInTime.js';

function getFilteredSortedDates(dates: Array<Date | null | undefined>) {
  return [...new Set(dates.filter(isNotNullish))].sort((a, b) => (a < b ? -1 : 1));
}

export type SupplementDatesSelectionSet = {
  signedAt: 1;
  startDateAG1: 1;
  startDateAG2: 1;
  endDateAG1: 1;
  endDateAG2: 1;
  startDateGeneral: 1;
  endDateGeneral: 1;
  changes: { path: 1 };
};

// TODO point based logic which time has as many active sections
export function getMinAndMaxDatesOfSupplement(
  supplement: NarrowedEntity<ContractSupplement, Omit<SupplementDatesSelectionSet, 'signedAt'>>,
): { start: Maybe<Date>; end: Maybe<Date> } {
  // filter out undefined dates and remove duplicates.
  // in most cases there should be one date left.
  const minStartdates: Array<Date> = getFilteredSortedDates([
    supplement.startDateGeneral,
    supplement.startDateAG1,
    supplement.startDateAG2,
  ]);
  const maxEnddates: Array<Date> = getFilteredSortedDates([
    supplement.endDateGeneral,
    supplement.endDateAG1,
    supplement.endDateAG2,
  ]);

  return {
    start: minStartdates.at(0),
    end: maxEnddates.at(-1),
  };
}

/**
 * Returns the number of active sections for a given date on a supplement.
 *
 * @param supplement The supplement to check.
 * @param date The date to check.
 * @returns The number of active sections for the given date.
 */
function getNumberOfActiveSectionsForDateOnSupplement(
  supplement: NarrowedEntity<ContractSupplement, SupplementDatesSelectionSet>,
  date: Date,
) {
  // ignore drafts as the we are not interested if the supplement is signed. We are only interested in theoretically active sections.
  return getActiveSectionsForPointInTime(supplement, date, { includeDrafts: true }).length;
}

export function getDateWithMostActiveSections(
  supplement: NarrowedEntity<ContractSupplement, SupplementDatesSelectionSet>,
): { start: Maybe<Date>; end: Maybe<Date> } {
  // filter out undefined dates and remove duplicates.
  // in most cases there should be one date left.
  const minStartdates: Array<Date> = getFilteredSortedDates([
    supplement.startDateGeneral,
    supplement.startDateAG1,
    supplement.startDateAG2,
  ]);
  const maxEnddates: Array<Date> = getFilteredSortedDates([
    supplement.endDateGeneral,
    supplement.endDateAG1,
    supplement.endDateAG2,
  ]);

  const result: { start: Maybe<Date>; end: Maybe<Date> } = { start: null, end: null };

  // find the start date with the most active sections
  let startDate = minStartdates.splice(0, 1).at(0);
  if (startDate) {
    // if there are dates left, we want to find the date with the most active sections
    while (minStartdates.length > 0) {
      const nextDate = minStartdates.splice(0, 1).at(0);
      if (nextDate) {
        const points = getNumberOfActiveSectionsForDateOnSupplement(supplement, nextDate);
        if (points > getNumberOfActiveSectionsForDateOnSupplement(supplement, startDate)) {
          startDate = nextDate;
        }
      }
    }

    result.start = startDate;
  }

  // find the end date with the most active sections
  let endDate = maxEnddates.splice(0, 1).at(0);
  if (endDate) {
    // if there are dates left, we want to find the date with the most active sections
    while (maxEnddates.length > 0) {
      const nextDate = maxEnddates.splice(0, 1).at(0);
      if (nextDate) {
        const points = getNumberOfActiveSectionsForDateOnSupplement(supplement, nextDate);
        if (points > getNumberOfActiveSectionsForDateOnSupplement(supplement, endDate)) {
          endDate = nextDate;
        }
      }
    }

    result.end = endDate;
  }

  return result;
}
